import { ActionTree } from 'vuex'
import sortBy from 'lodash.sortby'
import {
  CompanyHint,
  CreateCompanyHintRequest,
  UpdateCompanyHintRequest,
} from '@/models/hints'
import hintsApiService from '@/services/hints'
import { RootState } from '../../state'
import {
  HintsMutationTypes,
  HintsLoadSuccessMutationPayload,
  ViewHintSuccessMutationPayload,
} from './mutations'
import { HintsState } from './state'

export enum HintsActionTypes {
  Load = '[Hints Action] Load',
  Create = '[Hints Action] Create',
  Update = '[Hints Action] Update',
  Delete = '[Hints Action] Delete',
  ViewHint = '[Hints Action] View Hint',
}

export type UpdateHintActionPayload = {
  id: string
  data: UpdateCompanyHintRequest
}

export type ViewHintActionPayload = {
  hint: CompanyHint
}

export interface LoadScriptParams {
  scriptId?: string
  withContent?: boolean
}

export const actions: ActionTree<HintsState, RootState> = {
  async [HintsActionTypes.Load](context, params: LoadScriptParams) {
    const response = await hintsApiService.getHints(
      params.withContent,
      params.scriptId,
    )

    const payload: HintsLoadSuccessMutationPayload = {
      data: sortBy(response.data, 'views').reverse(),
    }
    context.commit(HintsMutationTypes.LoadSuccess, payload)

    return response
  },

  async [HintsActionTypes.Create](context, payload: CreateCompanyHintRequest) {
    const response = await hintsApiService.create(payload)

    const hints = context.getters.getCompanyHints as CompanyHint[]
    const newHints: HintsLoadSuccessMutationPayload = {
      data: [...hints, response.data],
    }
    context.commit(HintsMutationTypes.LoadSuccess, newHints)

    return response
  },

  async [HintsActionTypes.Update](context, payload: UpdateHintActionPayload) {
    const { id, data } = payload
    const response = await hintsApiService.update(id, data)

    const hints = [...context.getters.getCompanyHints] as CompanyHint[]
    const currentHintIndex = hints.findIndex(h => h.id === id)
    if (currentHintIndex >= 0) {
      const newHint = { ...hints[currentHintIndex] }
      newHint.title = data.title
      newHint.snippet = data.snippet
      newHint.content = data.content
      hints[currentHintIndex] = newHint

      const newHints: HintsLoadSuccessMutationPayload = {
        data: hints,
      }
      context.commit(HintsMutationTypes.LoadSuccess, newHints)
    }

    return response
  },

  async [HintsActionTypes.Delete](context, payload: string) {
    const response = await hintsApiService.delete(payload)

    const hints = context.getters.getCompanyHints as CompanyHint[]
    const newHints: HintsLoadSuccessMutationPayload = {
      data: hints.filter(h => h.id !== payload),
    }
    context.commit(HintsMutationTypes.LoadSuccess, newHints)

    return response
  },

  async [HintsActionTypes.ViewHint](context, payload: ViewHintActionPayload) {
    const { hint } = payload

    const response = await hintsApiService.viewHint(hint)

    const viewSuccessPayload: ViewHintSuccessMutationPayload = response
    context.commit(HintsMutationTypes.ViewHintSuccess, viewSuccessPayload)

    return response
  },
}
